import { useUser } from "@dashboard/auth";
import { hasPermission } from "@dashboard/auth/misc";
import {
  PermissionEnum,
  usePortalRosterQuery,
  usePortalShiftSwapAnswerMutation,
  usePortalShiftSwapRequestMutation,
} from "@dashboard/graphql";
import { Box, Button, Input, Option, Select, Text } from "@saleor/macaw-ui-next";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { ErrorText, Page, Section } from "../components";
import { portalMessages as m } from "../messages";
import { RosterGrid } from "../RosterGrid";
import { portalPaths } from "../urls";
import { addDays, formatDay, today, weekStart } from "../utils";

export const Roster = () => {
  const intl = useIntl();
  const { user } = useUser();
  const canManage = !!user && hasPermission(PermissionEnum.MANAGE_TIMEKEEPING, user);
  const [start, setStart] = useState(() => weekStart(today()));
  const end = addDays(start, 13);
  const days = Array.from({ length: 14 }, (_, i) => addDays(start, i));
  const { data, refetch } = usePortalRosterQuery({
    variables: { start, end },
    fetchPolicy: "cache-and-network",
  });
  const myId = data?.myEmployeeProfile?.userId;
  const lines = useMemo(() => data?.rosterBoard ?? [], [data]);
  const people = useMemo(() => {
    const names = new Map<string, string>();

    lines.forEach(line => names.set(line.userId, line.employeeName));

    return [...names].map(([userId, name]) => ({ userId, name }));
  }, [lines]);

  const [form, setForm] = useState({ date: "", colleagueId: "", colleagueDate: "", reason: "" });
  const [requestSwap, requestOpts] = usePortalShiftSwapRequestMutation({
    onCompleted: result => {
      if (!result.shiftSwapRequest?.errors.length) {
        setForm({ date: "", colleagueId: "", colleagueDate: "", reason: "" });
        refetch();
      }
    },
  });
  const [answer] = usePortalShiftSwapAnswerMutation({ onCompleted: () => refetch() });
  const myDays: Option[] = lines
    .filter(line => line.userId === myId && line.shift && line.date >= today())
    .map(line => ({ value: line.date, label: `${formatDay(line.date)} · ${line.shift?.name}` }));
  const colleagues: Option[] = people
    .filter(person => person.userId !== myId)
    .map(person => ({ value: person.userId, label: person.name }));

  return (
    <Page>
      <Section
        title={intl.formatMessage(m.roster)}
        actions={
          <Box display="flex" gap={2}>
            {canManage && (
              <Link to={portalPaths.rota}>
                <Button variant="primary" size="small" as="span">
                  {intl.formatMessage(m.shiftsPlannerModule)}
                </Button>
              </Link>
            )}
            <Button variant="secondary" size="small" onClick={() => setStart(addDays(start, -14))}>
              ‹
            </Button>
            <Button variant="secondary" size="small" onClick={() => setStart(addDays(start, 14))}>
              ›
            </Button>
          </Box>
        }
      >
        {lines.length === 0 && (
          <Text size={2} color="default2">
            {intl.formatMessage(canManage ? m.rosterEmptyManager : m.rosterEmpty)}
          </Text>
        )}
        {lines.length > 0 && (
          <RosterGrid
            days={days}
            people={people}
            highlightUserId={myId}
            cells={lines.map(line => ({
              userId: line.userId,
              date: line.date,
              label: line.shift
                ? `${line.shift.name} ${line.shift.startTime}–${line.shift.endTime}`
                : intl.formatMessage(m.dayOff),
              color: line.shift?.color,
              late: line.lateNotice,
            }))}
          />
        )}
        {lines.some(line => line.lateNotice) && (
          <Text size={1} color="default2">
            * {intl.formatMessage(m.lateChange)}
          </Text>
        )}
      </Section>
      {(myDays.length > 0 || (data?.myShiftSwaps.length ?? 0) > 0) && (
        <Section title={intl.formatMessage(m.mySwaps)}>
          {(data?.myShiftSwaps ?? []).map(swap => (
            <Box key={swap.id} display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Text size={2}>
                {swap.requesterName} ({formatDay(swap.requesterDate)}) ⇄ {swap.colleagueName} (
                {formatDay(swap.colleagueDate)}) —{" "}
                {intl.formatMessage(m.swapStatus, { status: swap.status.toLowerCase() })}
              </Text>
              {swap.canAnswer && (
                <>
                  <Button
                    size="small"
                    variant="primary"
                    onClick={() => answer({ variables: { id: swap.id, accept: true } })}
                  >
                    {intl.formatMessage(m.accept)}
                  </Button>
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={() => answer({ variables: { id: swap.id, accept: false } })}
                  >
                    {intl.formatMessage(m.refuse)}
                  </Button>
                </>
              )}
            </Box>
          ))}
          {myDays.length > 0 && (
            <>
              <Box
                display="grid"
                gap={2}
                __gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))"
              >
                <Select
                  size="small"
                  label={intl.formatMessage(m.myDate)}
                  options={myDays}
                  value={myDays.find(o => o.value === form.date) ?? null}
                  onChange={option => setForm({ ...form, date: option.value })}
                />
                <Select
                  size="small"
                  label={intl.formatMessage(m.colleague)}
                  options={colleagues}
                  value={colleagues.find(o => o.value === form.colleagueId) ?? null}
                  onChange={option => setForm({ ...form, colleagueId: option.value })}
                />
                <Input
                  size="small"
                  type="date"
                  label={intl.formatMessage(m.colleagueDate)}
                  value={form.colleagueDate}
                  onChange={event => setForm({ ...form, colleagueDate: event.target.value })}
                />
                <Input
                  size="small"
                  label={intl.formatMessage(m.reason)}
                  value={form.reason}
                  maxLength={500}
                  onChange={event => setForm({ ...form, reason: event.target.value })}
                />
              </Box>
              <Box>
                <Button
                  size="small"
                  variant="primary"
                  disabled={
                    !form.date || !form.colleagueId || !form.colleagueDate || requestOpts.loading
                  }
                  onClick={() => requestSwap({ variables: form })}
                >
                  {intl.formatMessage(m.askSwap)}
                </Button>
              </Box>
            </>
          )}
          <ErrorText errors={requestOpts.data?.shiftSwapRequest?.errors} />
        </Section>
      )}
    </Page>
  );
};
